import { useLayoutEffect, useRef, useState } from 'react'

import {
  choosePopoverPlacement,
  type PopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement/choose-popover-placement'

interface UseAbsolutePopoverPlacementArgs {
  popoverRef: React.RefObject<HTMLElement | null>
  gap?: number
}

interface AbsolutePopoverStyle {
  top?: string
  bottom?: string
  marginTop?: number
  marginBottom?: number
  maxHeight?: number
}

interface UseAbsolutePopoverPlacementResult {
  placement: PopoverPlacement
  isMeasured: boolean
  style: AbsolutePopoverStyle
}

/**
 * Placement for the `position: absolute` popover family, anchored to its own
 * `position: relative` offsetParent (the card/header row) rather than a
 * separate ref. Prefers opening downward (matching today's look) and flips
 * upward only when the card is too close to the bottom of its scroll
 * container's viewport.
 */
export const useAbsolutePopoverPlacement = ({
  popoverRef,
  gap = 4,
}: UseAbsolutePopoverPlacementArgs): UseAbsolutePopoverPlacementResult => {
  const [result, setResult] = useState<{
    placement: PopoverPlacement
    style: AbsolutePopoverStyle
  }>({ placement: 'bottom', style: { top: '100%', marginTop: gap } })
  const isMeasuredRef = useRef(false)

  useLayoutEffect(() => {
    const popoverEl = popoverRef.current
    const anchorEl = popoverEl?.offsetParent as HTMLElement | null
    if (!popoverEl || !anchorEl) return

    const anchorRect = anchorEl.getBoundingClientRect()
    const popoverHeight = popoverEl.getBoundingClientRect().height
    const viewportHeight = window.innerHeight

    const { placement, maxHeight } = choosePopoverPlacement({
      anchorTop: anchorRect.top,
      anchorBottom: anchorRect.bottom,
      popoverHeight,
      viewportHeight,
      preferred: 'bottom',
      gap,
    })

    isMeasuredRef.current = true
    setResult({
      placement,
      style: {
        ...(placement === 'bottom'
          ? { top: '100%', marginTop: gap }
          : { bottom: '100%', marginBottom: gap }),
        ...(maxHeight !== undefined ? { maxHeight } : {}),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only; callers mount this popover conditionally (open === mounted), and popoverRef/gap are stable
  }, [])

  return { ...result, isMeasured: isMeasuredRef.current }
}
